import { useState, useEffect, useMemo } from "react";
import {
  Plus, Search, Download, Trash2, FileText, FileImage, FileSpreadsheet,
  Presentation, FileArchive, File as FileIcon, UploadCloud, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { salesDocsApi } from "@/lib/unifiedApi";
import { storageService } from "@/lib/storage";
import { logActivity } from "@/lib/activityLog";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_CATEGORIES = ["โบรชัวร์", "ราคา", "สไลด์นำเสนอ", "ข้อมูลบริการ", "เทมเพลตสัญญา", "อื่นๆ"];
const MAX_MB = 25;
const ALLOWED = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "ppt", "pptx", "doc", "docx", "xls", "xlsx", "csv", "zip", "rar"];

const ext = (name) => (name || "").split(".").pop().toLowerCase();
const fmtSize = (b) => {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const EN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${d.getDate()} ${EN[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return iso; }
};

function FileTypeIcon({ name, className }) {
  const e = ext(name);
  const map = {
    pdf: FileText, png: FileImage, jpg: FileImage, jpeg: FileImage, gif: FileImage, webp: FileImage,
    ppt: Presentation, pptx: Presentation, doc: FileText, docx: FileText,
    xls: FileSpreadsheet, xlsx: FileSpreadsheet, csv: FileSpreadsheet, zip: FileArchive, rar: FileArchive,
  };
  const Icon = map[e] || FileIcon;
  const color = ["xls","xlsx","csv"].includes(e) ? "#16a34a"
    : ["ppt","pptx"].includes(e) ? "#ea580c"
    : ["doc","docx"].includes(e) ? "#2563eb"
    : ["png","jpg","jpeg","gif","webp"].includes(e) ? "#7c3aed"
    : ["zip","rar"].includes(e) ? "#64748b"
    : "#dc2626"; // pdf/other
  return <Icon size={22} className={className} style={{ color }} />;
}

// ─── Upload dialog ──────────────────────────────────────────
function UploadDialog({ open, onClose, categories }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!open) { setFile(null); setTitle(""); setCategory(""); setDescription(""); setProgress(0); setBusy(false); }
  }, [open]);

  const pickFile = (f) => {
    if (!f) return;
    if (!ALLOWED.includes(ext(f.name))) { toast.error("ชนิดไฟล์ไม่รองรับ"); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`ไฟล์ใหญ่เกิน ${MAX_MB}MB`); return; }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  async function upload() {
    if (!file) { toast.error("เลือกไฟล์ก่อน"); return; }
    if (!title.trim()) { toast.error("กรุณากรอกชื่อเอกสาร"); return; }
    setBusy(true);
    try {
      const res = await storageService.uploadFile(file, `sales_docs/${Date.now()}`, setProgress);
      await salesDocsApi.create({
        title: title.trim(),
        category: category.trim() || "อื่นๆ",
        description: description.trim(),
        file_url: res.url,
        file_name: res.name,
        file_path: res.path,
        file_size: file.size,
        file_type: ext(file.name),
        uploaded_by: user?.name || "unknown",
      });
      logActivity(user?.id, user?.name, "salesdoc_upload", `${title.trim()} (${category.trim() || "อื่นๆ"})`);
      toast.success("อัปโหลดสำเร็จ");
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("อัปโหลดไม่สำเร็จ: " + e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg">
        <DialogHeader><DialogTitle>อัปโหลดเอกสาร</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {/* File picker */}
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400">
            <input type="file" className="hidden"
              accept={ALLOWED.map((x) => "." + x).join(",")}
              onChange={(e) => pickFile(e.target.files?.[0])} />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-slate-700">
                <FileTypeIcon name={file.name} /> <span className="text-sm">{file.name}</span>
                <span className="text-xs text-slate-400">({fmtSize(file.size)})</span>
              </div>
            ) : (
              <div className="text-slate-400">
                <UploadCloud size={28} className="mx-auto mb-2" />
                <div className="text-sm">คลิกเพื่อเลือกไฟล์ (PDF / รูป / PPT / Word / Excel / ZIP)</div>
                <div className="text-xs mt-1">สูงสุด {MAX_MB}MB</div>
              </div>
            )}
          </label>

          <div className="space-y-2">
            <Label className="text-slate-500">ชื่อเอกสาร <span className="text-red-500">*</span></Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="เช่น โบรชัวร์บริการถ่ายภาพ 2026" className="bg-white border-slate-200 text-slate-800" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">หมวดหมู่</Label>
            <Input list="salesdoc-categories" value={category} onChange={(e) => setCategory(e.target.value)}
              placeholder="เลือกหรือพิมพ์หมวดใหม่" className="bg-white border-slate-200 text-slate-800" />
            <datalist id="salesdoc-categories">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">คำอธิบาย</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="(ไม่บังคับ)" className="bg-white border-slate-200 text-slate-800" />
          </div>

          {busy && (
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} className="text-slate-500">ยกเลิก</Button>
          <Button onClick={upload} disabled={busy} style={{ backgroundColor: "#2563eb", color: "#fff" }}>
            {busy ? `กำลังอัปโหลด... ${Math.round(progress)}%` : "อัปโหลด"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ───────────────────────────────────────────────────
export default function DocumentsPage() {
  const { isAdmin, user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    const unsub = salesDocsApi.subscribe((data) => {
      data.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setDocs(data);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const categories = useMemo(() => {
    const used = docs.map((d) => d.category).filter(Boolean);
    return [...new Set([...DEFAULT_CATEGORIES, ...used])];
  }, [docs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (catFilter && d.category !== catFilter) return false;
      if (!q) return true;
      return (d.title || "").toLowerCase().includes(q) ||
        (d.description || "").toLowerCase().includes(q) ||
        (d.file_name || "").toLowerCase().includes(q);
    });
  }, [docs, search, catFilter]);

  async function remove(d) {
    if (!window.confirm(`ลบ "${d.title}" ?`)) return;
    try {
      if (d.file_path) { try { await storageService.deleteFile(d.file_path); } catch {} }
      await salesDocsApi.delete(d.id);
      logActivity(user?.id, user?.name, "salesdoc_delete", d.title);
      toast.success("ลบแล้ว");
    } catch { toast.error("ลบไม่สำเร็จ"); }
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText size={24} className="text-blue-600" />Documents
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">เอกสารขาย product/service — ดาวน์โหลดไปใช้งานได้</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => setUploadOpen(true)}
            style={{ backgroundColor: "#2563eb", color: "#fff" }} className="font-semibold">
            <Plus size={16} className="mr-1" />อัปโหลดเอกสาร
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาชื่อเอกสาร" className="pl-9 bg-white border-slate-200 text-slate-800" />
        </div>
        <button onClick={() => setCatFilter("")}
          className={`px-3 py-1.5 rounded-full text-xs border ${!catFilter ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500 border-slate-200"}`}>
          ทั้งหมด
        </button>
        {categories.map((c) => (
          <button key={c} onClick={() => setCatFilter(c)}
            className={`px-3 py-1.5 rounded-full text-xs border ${catFilter === c ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
            {c}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-400">กำลังโหลด...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            {docs.length === 0 ? (isAdmin() ? 'ยังไม่มีเอกสาร — กด "อัปโหลดเอกสาร" เพื่อเริ่มต้น' : "ยังไม่มีเอกสาร") : "ไม่พบเอกสารที่ค้นหา"}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((d) => (
              <div key={d.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0">
                  <FileTypeIcon name={d.file_name} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-800 font-medium truncate">{d.title}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-2 flex-wrap">
                    <span className="bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{d.category || "อื่นๆ"}</span>
                    <span className="uppercase">{d.file_type}</span>
                    <span>{fmtSize(d.file_size)}</span>
                    <span>· {fmtDate(d.created_at)}</span>
                    {d.uploaded_by && <span>· {d.uploaded_by}</span>}
                  </div>
                  {d.description && <div className="text-xs text-slate-400 truncate mt-0.5">{d.description}</div>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <a href={d.file_url} target="_blank" rel="noopener noreferrer"
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="เปิดดู">
                    <ExternalLink size={16} />
                  </a>
                  <a href={d.file_url} target="_blank" rel="noopener noreferrer" download={d.file_name}
                    className="p-2 rounded-lg hover:bg-blue-50 text-blue-600" title="ดาวน์โหลด">
                    <Download size={16} />
                  </a>
                  {isAdmin() && (
                    <button onClick={() => remove(d)}
                      className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500" title="ลบ">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} categories={categories} />
    </div>
  );
}
